/**
 * Vehicle view — Stage 4a fleet art.
 *
 * Replaces the grey-box primitives with generated GLB vehicle models. The pool
 * is fixed-size: one Group per threat slot + one per ambient slot, all
 * pre-allocated in the constructor from the asset registry templates. `step`
 * captures the previous sim-step pose (call before sim.step); `sync` toggles
 * visibility + copies interpolated poses each render frame — zero allocation
 * in steady state after warmup.
 *
 * Model selection: owned by the SIM. `VehicleEntity.model` is picked at spawn
 * (deterministic hash on the pool slot id) so the sim can size that vehicle's
 * Rapier collider and crash extents from the model the player actually sees.
 * The view just reads it.
 *
 * NOTHING IS BUILT DURING A RUN (2026-08-01 spawn-clone pass)
 * ----------------------------------------------------------
 * Every VIEW a slot can ever need is cloned from the templates in the
 * CONSTRUCTOR, and a spawn only toggles `visible` and swaps a cached material
 * reference. The set is small and exactly knowable: the sim derives a
 * vehicle's model from `pickVehicleModel(kind, slotIndex)` — the slot index is
 * fixed, so a slot can only ever be handed the ONE car model and the ONE truck
 * model that hash out of its own index. Two views per slot, 80 for the whole
 * 40-slot pool, three Object3D nodes each (the GLBs are single-mesh), all
 * sharing the templates' geometry and the run-long paint-material cache.
 *
 * WHAT THAT FIXED. `rebuildSlot` used to `template.group.clone(true)` on EVERY
 * spawn (~4/s at current density) — and the death branch of `sync` cleared
 * `slot.model`, so the "same model, just repaint" fast path below was DEAD
 * CODE: measured 19 rebuilds for 19 spawns, 0 repaints. The clone itself is
 * three small objects, but each fresh Mesh is a fresh key in the renderer's
 * per-object caches: WebGPU allocates a new render object, a new per-object
 * uniform buffer and new bind groups for it on the frame it first draws, and
 * abandons the previous ones to the GC. That churn is what put ~2.5 dropped
 * frames per 1000 inside spawn windows against ~1.5 outside. Reusing the same
 * Mesh instances forever means those GPU-side per-object resources are
 * allocated once, during the load-screen warm-up, and never again.
 *
 * A slot keeps its built state when its vehicle dies — only `visible` goes
 * false — so a respawn of the same model takes the repaint-only path.
 *
 * Paint (2026-07-31 cartoon pass)
 * -------------------------------
 * The old look was a per-id HSL nudge at 12% saturation, i.e. a fleet of
 * off-whites. Now each vehicle takes one of 16 saturated arcade colours from
 * the SIM's per-spawn paint draw (`VehicleEntity.paint`); trucks draw from a
 * smaller subset. The draw moved into the sim on the 2026-08-01 variety pass —
 * see `pickVehicleColor` for the repetition bug that forced it.
 *
 * Applying it is NOT a colour multiply. Every shipped vehicle GLB has exactly
 * one material — white baseColorFactor over a baseColor atlas that ALREADY
 * carries an authored body colour (the sedan is blue, the hatchback red…)
 * alongside dark glass, black tyres and grey rims. Multiplying a palette
 * colour over that only tints what is already there, and turns the light grey
 * rims into the palette colour (the first attempt gave a blue car pink
 * wheels). Instead `vehiclePaintMaterial` (render/plastic.ts) swaps hue +
 * saturation on the SATURATED texels only, preserving each texel's value, so
 * the body repaints cleanly and the glass/tyres/rims pass through untouched.
 *
 * Orientation: each template wrapper already yaws the model so its nose faces
 * +Z (threat travel direction). Driving vehicles use position + yaw (added on
 * top of the wrapper's baked yaw via the inner source rotation); kicked
 * vehicles use position + full quaternion (full 6DOF tumble). Colliders stay
 * as Rapier cuboids (unchanged in the sim).
 *
 * Grounding: the template pivot is the model's bbox CENTER (assets.ts applies
 * no ground lift), so a driving slot sits at y = template.halfHeight (wheels
 * at y=0) and a kicked slot sits at y = v.y (the mirrored Rapier body center),
 * which makes the tumble pivot correctly and follow the true ballistic arc.
 */

import {
  Group,
  Mesh,
  Vector3,
  type Material,
  type Object3D,
  type Texture,
} from "three/webgpu";
import { Quaternion } from "three/webgpu";
import type { WorldState } from "../sim/types";
import { isLive, pickVehicleModel, vehicleLength, vehicleWidth } from "../sim/vehicles";
import type { AssetRegistry, VehicleModel, VehicleTemplate } from "./assets";
import { curveOffset, curveHeading } from "./curve";
import { vehiclePaintMaterial } from "./plastic";
import { TUNING } from "../sim/tuning";

/**
 * Blink rate (Hz) of a settled wreck's blink-out — one ON+OFF cycle per 1/9 s,
 * i.e. a visibility toggle every 1/18 s (~3.3 frames at 60 Hz). Fast enough to
 * read as an arcade "this is leaving now" flicker, slow enough that the toggle
 * survives a dropped frame.
 */
const WRECK_BLINK_HZ = 9;

/**
 * Is a blinking wreck visible this frame? `fade` is VehicleEntity.wreckFade,
 * the sim's countdown from TUNING.kick.wreckFadeTime to 0 (armed
 * TUNING.kick.wreckFadeDelay after the wreck's first ground impact — the wreck
 * is usually still grinding and toppling while it flashes).
 *
 * Deterministic: a pure function of a sim value, so two replays of a seed blink
 * the same. Starts VISIBLE (elapsed 0 is an even interval) and ends HIDDEN,
 * which holds whenever the countdown spans an EVEN number of 1/18 s intervals:
 * the last interval index is then odd. 1.0 s was 18 of them; the current
 * 1.6667 s (30/18) is 30, so the final ~1/18 s is dark and the wreck is already
 * off screen when the despawn lands.
 */
export function wreckBlinkVisible(fade: number): boolean {
  const elapsed = TUNING.kick.wreckFadeTime - fade;
  const interval = Math.floor(elapsed * 2 * WRECK_BLINK_HZ);
  return interval % 2 === 0;
}

/** Yaw -π: sim yaw convention (π = facing +Z) -> wrapper frame correction. */
const YAW_CORR = new Quaternion(0, -1, 0, 0);
/** Scratch quaternion for kicked-body mirroring (no per-frame allocation). */
const _bodyQuat = new Quaternion();
/** Scratch quaternion for the curve yaw premultiply on kicked vehicles. */
const _curveYawQuat = new Quaternion();
/** World +Y axis for the curve yaw quaternion (no per-frame allocation). */
const _Y = new Vector3(0, 1, 0);

/** Ambient boxes are smaller + dimmer (scenic shorthand) — still use GLBs but scaled down. */
const AMBIENT_SCALE = 0.7;

/**
 * One pre-built clone of one MODEL, owned by one slot for the life of the run.
 * `meshes`/`maps` are flattened at build time so a repaint is an indexed loop
 * rather than a `traverse` with a closure.
 */
interface SlotView {
  /** The cloned template wrapper (child of the slot Group). Hidden when idle. */
  root: Group;
  /** Every material-bearing mesh in `root`, in traversal order. */
  meshes: Mesh[];
  /** Each mesh's TEMPLATE baseColor map — the paint-cache key (parallel array). */
  maps: (Texture | null)[];
  /** The template's own half-height (before any ambient scale). */
  halfHeight: number;
}

interface SlotState {
  /** The slot's Group (child of this.group). Hidden when dead. */
  group: Group;
  /**
   * Every view this slot can show, keyed by model. Pre-built in the
   * constructor for the (car, truck) pair the sim can hand this slot index;
   * `viewFor` adds any other model ONCE, defensively, if the sim's selection
   * rule ever changes under us.
   */
  views: Map<VehicleModel, SlotView>;
  /** The view currently shown (null until the slot's first spawn). */
  active: SlotView | null;
  /** Current model key (or null if the slot is dead/uninitialized). */
  model: VehicleModel | null;
  /** Current vehicle id (so we rebuild only when the id changes, not every frame). */
  vehicleId: number;
  /** Whether this slot is currently built as an ambient (scaled) instance. */
  ambient: boolean;
  /**
   * Visual half-height of the built template (REAL_DIMS height / 2, times the
   * ambient scale when scaled down). The template pivot is the model's bbox
   * CENTER, so a driving vehicle sits at position.y = halfHeight to plant its
   * wheels exactly at y=0.
   */
  halfHeight: number;
  /**
   * Painted materials this slot currently draws with. They are NOT owned by the
   * slot: `VehicleView.paintCache` owns one material per (model, palette
   * colour) for the whole run, so a respawn never builds a node material — see
   * `paintFor`.
   */
  tintedMats: Material[];
  /** Palette colour this slot is currently painted with (-1 = none built). */
  paint: number;
  /** Previous sim-step z (for render interpolation). */
  prevZ: number;
  /** Previous sim-step x (for render interpolation). */
  prevX: number;
  /** Vehicle id at the previous sim step (to detect spawn transitions). */
  prevId: number;
}

/**
 * Pooled vehicle view. Add `group` to the scene; call `sync` each render
 * frame with the current WorldState. Requires the asset registry to be
 * loaded before construction.
 */
export class VehicleView {
  readonly group: Group;
  private readonly slots: SlotState[] = [];
  private readonly registry: AssetRegistry;
  /**
   * One painted material per (model, palette colour), for the life of the run.
   *
   * PERF (2026-08-01): `rebuildSlot` used to build a fresh
   * `vehiclePaintMaterial` — a TSL node material — on every spawn, and dispose
   * the previous one. Each new node material is a NEW WebGPU pipeline the first
   * time it is drawn, so every unseen (model, colour) pair stalled the frame it
   * appeared in; the measured cost was the single biggest hitch in a run
   * (multi-frame spikes clustered in the first ~30 s, then again at each new
   * model). The set stays tiny and bounded by PALETTE SIZE × MODELS — 3 car
   * models × 16 + 2 truck models × 6 = 60 after the 2026-08-01 variety pass —
   * and the paint colour is a material UNIFORM, not a pipeline variant, so more
   * colours cost cache entries, not compiles, and a spawn never recompiles
   * anything. The materials are cached, shared and warmed up during LOADING
   * (see `warmupMeshes`). Nothing is disposed until the view is.
   */
  private readonly paintCache = new Map<string, Material>();

  constructor(maxThreat: number, maxAmbient: number, registry: AssetRegistry) {
    this.group = new Group();
    this.group.name = "vehicleView";
    this.registry = registry;

    const total = maxThreat + maxAmbient;
    for (let i = 0; i < total; i++) {
      const g = new Group();
      g.visible = false;
      g.frustumCulled = false; // poses change every frame
      this.group.add(g);
      const slot: SlotState = {
        group: g,
        views: new Map(),
        active: null,
        model: null,
        vehicleId: -1,
        ambient: false,
        halfHeight: 0,
        tintedMats: [],
        paint: -1,
        prevZ: 0,
        prevX: 0,
        prevId: -1,
      };
      this.slots.push(slot);
      // Pre-build the ONLY two models this slot can ever be handed. The sim
      // picks `pickVehicleModel(kind, slotIndex)` at spawn and the slot index
      // is fixed for the run, so the car branch and the truck branch each
      // resolve to exactly one model — nothing else can turn up here, and
      // nothing has to be cloned once the run starts.
      this.viewFor(slot, pickVehicleModel("car", i));
      this.viewFor(slot, pickVehicleModel("truck", i));
    }
  }

  /**
   * Capture the previous sim-step pose for every slot. Call this from the sim
   * callback BEFORE `sim.step()` (mirroring cameraRig.step / prevPlayer), so
   * `sync` can interpolate between the previous and current tick. Zero
   * allocation: writes into the pre-allocated slot fields.
   */
  step(state: WorldState): void {
    const vehicles = state.vehicles;
    const total = this.slots.length;
    for (let i = 0; i < total; i++) {
      const slot = this.slots[i];
      const v = vehicles[i];
      if (v && isLive(v)) {
        slot.prevZ = v.z;
        slot.prevX = v.x;
        slot.prevId = v.id;
      } else {
        slot.prevId = -1;
      }
    }
  }

  /**
   * Sync meshes from the sim vehicle list. Threats occupy [0, maxThreat),
   * ambients [maxThreat, total). Driving vehicles: position + yaw. Kicked
   * vehicles: position + full quaternion. Dead slots are hidden.
   *
   * Position is interpolated between the previous and current sim tick using
   * `alpha` (the render-frame interpolation fraction), so vehicles move
   * smoothly at any display refresh rate instead of stepping at the 60 Hz sim
   * rate. If a vehicle just spawned (prevId mismatch), it snaps to current.
   *
   * `distance` is the interpolated player travel distance; the curve offset
   * (curveOffset) and absolute curve heading (curveHeading) are computed from the
   * INTERPOLATED vehicle z so they stay smooth alongside the position. Driving
   * vehicles add the yaw delta to the existing v.yaw - π; kicked vehicles
   * premultiply a Y-quaternion of the delta so the tumble tracks the road.
   */
  sync(state: WorldState, alpha: number, distance: number): void {
    const vehicles = state.vehicles;
    const total = this.slots.length;
    for (let i = 0; i < total; i++) {
      const slot = this.slots[i];
      const v = vehicles[i];
      if (!v || !isLive(v)) {
        // Hide ONLY. The slot keeps its built state (model, ambient flag,
        // paint) so the next occupant of the same model takes the repaint-only
        // path below. Clearing `slot.model` here is what used to force a full
        // rebuild — and therefore a template clone — on every single respawn.
        slot.group.visible = false;
        slot.vehicleId = -1;
        continue;
      }
      // BLINK-OUT. A grounded wreck runs a sim countdown (VehicleEntity.
      // wreckFade, TUNING.kick.wreckFadeTime, armed wreckFadeDelay after
      // touchdown) before it is despawned; while it runs, the wreck flickers —
      // usually while it is still spinning out and toppling.
      //
      // VISIBILITY ONLY — never opacity. Touching `material.opacity` /
      // `.transparent` / `.needsUpdate` re-derives the TSL node material and
      // hands WebGPU a new pipeline to compile, which is precisely the stall the
      // 2026-08-01 paint-cache pass removed. Toggling `Group.visible` costs a
      // draw-call skip and nothing else.
      //
      // Phase comes from the sim value, so every replay of a seed blinks
      // identically: elapsed 0 → wreckFadeTime maps to BLINK_TOGGLES intervals
      // and the LAST interval is hidden, so the wreck is already gone from the
      // screen on the frame the despawn lands.
      slot.group.visible = v.wreckFade > 0 ? wreckBlinkVisible(v.wreckFade) : true;

      // Rebuild the slot's mesh when the entity in it changes shape: a
      // different MODEL (the sim picks it at spawn and exposes it on the
      // entity), a different ambient flag, or a fresh slot. Comparing
      // `slot.model !== v.model` is what catches a recycled slot whose new
      // occupant is a different vehicle — comparing ids could not, because a
      // threat's id IS its pool slot index and therefore never changes.
      const isAmbient = v.role === "ambient";
      const paint = pickVehicleColor(v.paint, v.model);
      if (slot.model !== v.model || slot.ambient !== isAmbient) {
        // Shape change: show a DIFFERENT pre-built view. Two visibility flags,
        // a scale write and a repaint — no clone, no scene-graph surgery.
        this.activateSlot(slot, v.model, isAmbient, v.id, paint);
      } else if (slot.paint !== paint) {
        // Same shape, new occupant, new colour. The paint is drawn PER SPAWN
        // and a threat slot's model rarely changes (it is derived from the slot
        // id and only flips when the slot swaps between car and truck), so this
        // is the path almost every respawn takes: assign a cached material to
        // each of the view's meshes and nothing else.
        this.repaintSlot(slot, v.model, paint);
        slot.vehicleId = v.id;
      }

      // Interpolated position: lerp prev→current by alpha. Snap (no lerp) if
      // the vehicle just spawned into this slot (prevId mismatch) or is kicked
      // (Rapier owns the motion; the sim mirrors it at 60 Hz and the quaternion
      // already encodes the full pose — interpolating only position would
      // desync from the body's actual orientation).
      let x = v.x;
      let z = v.z;
      if (slot.prevId === v.id && v.motion !== "kicked") {
        x = slot.prevX + (v.x - slot.prevX) * alpha;
        z = slot.prevZ + (v.z - slot.prevZ) * alpha;
      }
      // Render-only curvature: shift x by the curve offset at this vehicle's
      // (interpolated) world z, and add the yaw delta. Both are 0 at the
      // player's position (z=0, r=D), so the cockpit stays centered.
      const off = curveOffset(z, distance);
      // Y: the template pivot is the model's bbox CENTER.
      //  - driving: raise by the visual half-height → wheels plant at y=0.
      //  - kicked: use the physics body center y directly, so the tumble
      //    pivots about the center and follows the real ballistic arc
      //    (including ground contact) instead of hovering at a constant Y.
      const y = v.motion === "kicked" && v.quat ? v.y : slot.halfHeight;
      slot.group.position.set(x + off, y, z);
      if (v.motion === "kicked" && v.quat) {
        // Kicked: premultiply a Y-quaternion of the curve yaw delta so the
        // tumble tracks the curving road, then the -π convention correction,
        // then the body's mirrored quaternion (continuity with the driving
        // pose at the moment of conversion is preserved).
        const yawDelta = curveHeading(z, distance);
        _curveYawQuat.setFromAxisAngle(_Y, yawDelta);
        slot.group.quaternion
          .copy(_curveYawQuat)
          .multiply(YAW_CORR)
          .multiply(_bodyQuat.set(v.quat[0], v.quat[1], v.quat[2], v.quat[3]));
      } else {
        // Sim yaw convention: π = facing +Z (oncoming threat/ambient),
        // 0 = facing -Z (same-direction ambient). The wrapper bakes nose→+Z
        // at slot rotation 0, so slot rotation = v.yaw - π. This fixes the
        // double-rotation that had every vehicle driving rear-first. The
        // curve yaw delta is added on top so the vehicle follows the bend.
        slot.group.rotation.set(0, v.yaw - Math.PI + curveHeading(z, distance), 0);
      }
    }
  }

  /** Release GPU resources held by tinted material clones (templates are owned by AssetManager). */
  dispose(): void {
    for (const slot of this.slots) {
      slot.tintedMats.length = 0;
      // The pre-built views share the templates' geometry (owned by
      // AssetManager) and the paint cache's materials (disposed below), so
      // dropping the scene-graph references is all that is owed here.
      slot.views.clear();
      slot.active = null;
      slot.group.clear();
      this.group.remove(slot.group as unknown as Object3D);
    }
    for (const m of this.paintCache.values()) m.dispose();
    this.paintCache.clear();
    this.slots.length = 0;
  }

  // -----------------------------------------------------------------------
  // Internals
  // -----------------------------------------------------------------------

  /**
   * The slot's pre-built view for `model`, cloning the template ONCE if this
   * pair has somehow never been seen (it should not be: the constructor builds
   * both models the sim's selection rule can produce for this slot index —
   * this is the safety net for a future rule change, and it still clones at
   * most once per (slot, model) for the whole run, never per spawn).
   */
  private viewFor(slot: SlotState, model: VehicleModel): SlotView {
    const existing = slot.views.get(model);
    if (existing) return existing;

    const template: VehicleTemplate = this.registry.vehicles[model];
    const root = template.group.clone(true);
    root.visible = false;
    const meshes: Mesh[] = [];
    const maps: (Texture | null)[] = [];
    root.traverse((o) => {
      const m = o as Mesh;
      if (!m.material || Array.isArray(m.material)) return;
      meshes.push(m);
      maps.push((m.material as Material & { map?: Texture | null }).map ?? null);
      // 2026-08-01: vehicles CAST. They are the objects the player is judging
      // distance against, and a car with no shadow reads as hovering. They also
      // receive, so a truck darkens the car beside it.
      m.castShadow = true;
      m.receiveShadow = true;
    });
    slot.group.add(root);
    const view: SlotView = { root, meshes, maps, halfHeight: template.halfHeight };
    slot.views.set(model, view);
    return view;
  }

  /**
   * Point a slot at one of its pre-built views. This is the whole of what a
   * spawn costs now: hide the outgoing view, show the incoming one, write its
   * scale, and assign cached paint materials. No clone, no `add`/`remove`, no
   * material construction — the scene graph is identical from one run-second
   * to the next.
   */
  private activateSlot(
    slot: SlotState,
    model: VehicleModel,
    ambient: boolean,
    vehicleId: number,
    paint: number,
  ): void {
    const view = this.viewFor(slot, model);
    if (slot.active && slot.active !== view) slot.active.root.visible = false;
    view.root.visible = true;
    view.root.scale.setScalar(ambient ? AMBIENT_SCALE : 1);
    slot.active = view;
    // Ground offset for this build: the template pivot is the bbox center.
    slot.halfHeight = view.halfHeight * (ambient ? AMBIENT_SCALE : 1);
    // Defensive: a recycled slot may still carry a kicked vehicle's tumble
    // quaternion. Reset to identity so a freshly spawned driving vehicle never
    // inherits a stale orientation (sync() only writes rotation for driving).
    slot.group.quaternion.identity();
    slot.group.rotation.set(0, 0, 0);

    slot.model = model;
    slot.vehicleId = vehicleId;
    slot.ambient = ambient;
    this.repaintSlot(slot, model, paint);
  }

  /**
   * Put the active view onto a palette colour. `vehiclePaintMaterial` does an
   * HSV hue/saturation swap on the saturated (body-paint) texels only, which is
   * what keeps glass and tyres dark; see render/plastic.ts for why a plain
   * colour multiply cannot work on these albedo atlases. The cache key is each
   * mesh's TEMPLATE map, captured once in `viewFor`, so repainting a repainted
   * view stays stable.
   */
  private repaintSlot(slot: SlotState, model: VehicleModel, paint: number): void {
    const view = slot.active;
    if (!view) return;
    slot.tintedMats.length = 0;
    for (let i = 0; i < view.meshes.length; i++) {
      const out = this.paintFor(model, paint, view.maps[i]);
      view.meshes[i].material = out;
      if (!slot.tintedMats.includes(out)) slot.tintedMats.push(out);
    }
    slot.paint = paint;
  }

  /** Cached painted material for a (model, colour) pair; builds it on miss. */
  private paintFor(model: VehicleModel, paint: number, map: Texture | null): Material {
    const key = `${model}|${paint}`;
    let mat = this.paintCache.get(key);
    if (!mat) {
      mat = vehiclePaintMaterial(map, paint);
      this.paintCache.set(key, mat);
    }
    return mat;
  }

  /**
   * LOADING-time warm-up over the REAL pool.
   *
   * The old warm-up compiled every (model, palette colour) pipeline on
   * THROWAWAY clones. That covered the shader pipelines, but every per-object
   * GPU resource it built was discarded with the clones — which was fine while
   * gameplay also built fresh clones, and is exactly the wrong shape now that
   * the pool is fixed. So the warm-up drives the pool itself: every pre-built
   * view is parked in front of the camera and drawn, so its render object,
   * uniform buffer and bind groups — colour pass AND shadow pass — are the ones
   * gameplay goes on to use.
   *
   * Coverage takes more than one draw, because a view has ONE model but its
   * model has up to sixteen palette colours. Each pass paints the k-th view of
   * a model with colour `(p * viewCount + k) % paletteLength`, so after
   * `passes = max(ceil(paletteLength / viewCount))` passes every (model,
   * colour) material has been drawn at least once. With 40 slots that is two
   * passes (each car model owns ~13 views against a 16-colour palette).
   *
   * The caller renders one frame per pass, then calls `restore`.
   */
  warmup(x: number, y: number, z: number): VehicleWarmup {
    // Group the pre-built views by model so a pass can stride the palette.
    const byModel = new Map<VehicleModel, SlotView[]>();
    for (const slot of this.slots) {
      for (const [model, view] of slot.views) {
        const list = byModel.get(model);
        if (list) list.push(view);
        else byModel.set(model, [view]);
      }
    }
    let passes = 1;
    for (const [model, views] of byModel) {
      const table = isTruckModel(model) ? TRUCK_PALETTE : VEHICLE_PALETTE;
      passes = Math.max(passes, Math.ceil(table.length / Math.max(1, views.length)));
    }

    // Park the whole pool in front of the camera, inside the shadow frustum.
    this.group.position.set(x, y, z);
    for (const slot of this.slots) {
      slot.group.visible = true;
      slot.group.position.set(0, 0, 0);
      slot.group.quaternion.identity();
      slot.group.rotation.set(0, 0, 0);
      for (const view of slot.views.values()) {
        view.root.visible = true;
        view.root.scale.setScalar(1);
        for (const m of view.meshes) m.frustumCulled = false;
      }
    }

    const beginPass = (p: number): void => {
      for (const [model, views] of byModel) {
        const table = isTruckModel(model) ? TRUCK_PALETTE : VEHICLE_PALETTE;
        for (let k = 0; k < views.length; k++) {
          const paint = table[(p * views.length + k) % table.length];
          const view = views[k];
          for (let i = 0; i < view.meshes.length; i++) {
            view.meshes[i].material = this.paintFor(model, paint, view.maps[i]);
          }
        }
      }
    };

    const restore = (): void => {
      this.group.position.set(0, 0, 0);
      for (const slot of this.slots) {
        slot.group.visible = false;
        for (const view of slot.views.values()) {
          view.root.visible = false;
          for (const m of view.meshes) m.frustumCulled = true;
        }
        // Force the next `sync` to re-activate from scratch — which is now a
        // visibility toggle and a repaint, not a build.
        slot.active = null;
        slot.model = null;
        slot.paint = -1;
        slot.tintedMats.length = 0;
      }
    };

    return { passes, beginPass, restore };
  }
}

/** Handle returned by {@link VehicleView.warmup}; see that method. */
export interface VehicleWarmup {
  /** Number of paint passes the caller must render (>= 1). */
  readonly passes: number;
  /** Paint every pre-built view for pass `p` (0-based). */
  beginPass(p: number): void;
  /** Hide the pool again and hand it back to `sync`. */
  restore(): void;
}

// ---------------------------------------------------------------------------
// Curated arcade paint palette (stable per vehicle id).
// ---------------------------------------------------------------------------

/**
 * The fleet palette: sixteen punchy, mid-to-high-chroma candy colours that read
 * instantly against the flat grey road and the cel-shaded world. Cars pick from
 * all sixteen.
 *
 * Grown from 10 to 16 on the 2026-08-01 variety pass (owner playtest: "lots of
 * same-type vehicle chains all with the same colour"). The primary fix was
 * making the draw PER SPAWN (see `pickVehicleColor` + sim `paintDraw`); the six
 * additions fill the hue gaps the original ten left — the wheel had a 78° hole
 * between green and teal, a 72° hole between purple and pink, and nothing at
 * all in cyan — so a bigger fleet no longer means near-duplicate neighbours.
 * Every new entry is ≥ 25° of hue from its nearest existing neighbour, or
 * separated from it by a large value/saturation step (latte vs orange).
 */
export const VEHICLE_PALETTE: readonly number[] = [
  0xe63946, // red
  0xf4a261, // orange
  0xffd166, // yellow
  0x2a9d8f, // teal
  0x457b9d, // steel blue
  0x3a86ff, // blue
  0x8338ec, // purple
  0xff5d8f, // pink
  0x90be6d, // green
  0xf1f1f1, // white
  // --- 2026-08-01 variety pass -------------------------------------------
  0xff6b57, // coral      — warm red-orange, lighter than `red`
  0xc5d86d, // pistachio  — the yellow→green gap
  0x3ddc84, // spring     — the green→teal gap
  0x00b4d8, // cyan       — the teal→steel gap
  0xd45db8, // orchid     — the purple→pink gap
  0xb08968, // latte      — desaturated warm brown (bronze/tan bodies)
];

/**
 * Trucks bias to a smaller, more "commercial" subset — a purple semi reads as
 * a toy, and the big silhouettes are on screen longest, so they get the
 * colours that stay believable at size. Cyan and latte joined on the variety
 * pass: both are ordinary haulage colours at scale.
 */
export const TRUCK_PALETTE: readonly number[] = [
  0xf1f1f1, // white
  0xe63946, // red
  0x2a9d8f, // teal
  0xffd166, // yellow
  0x00b4d8, // cyan
  0xb08968, // latte
];

/** True for the two truck models (they use the reduced palette). */
export function isTruckModel(model: VehicleModel): boolean {
  return model === "boxtruck" || model === "semi";
}

/**
 * Palette pick for a vehicle from its PER-SPAWN paint draw
 * (`VehicleEntity.paint`, sim-side `paintDraw`), folded onto the model's table.
 *
 * WHY THE DRAW MOVED (2026-08-01, owner playtest "lots of same-type vehicle
 * chains all with the same colour"). This function used to hash (vehicle id,
 * model). Both of those are FIXED FOR THE WHOLE RUN: a threat's id is its pool
 * slot index, and `spawnVehicle` derives its model from that same index. So
 * every slot had exactly one paint job for the entire run, and since traffic
 * groups are handed consecutive slots round-robin, the same short cycle of
 * colours came back group after group. The sim now draws a fresh unit float on
 * every spawn and the render just maps it — still deterministic per seed (the
 * draw is a pure function of the spawn sequence), but a recycled slot comes
 * back in a different colour.
 *
 * @param paint 0..1 draw from `VehicleEntity.paint`.
 */
export function pickVehicleColor(paint: number, model: VehicleModel): number {
  const table = isTruckModel(model) ? TRUCK_PALETTE : VEHICLE_PALETTE;
  const p = paint < 0 ? 0 : paint >= 1 ? 0.999999 : paint;
  return table[Math.min(table.length - 1, Math.floor(p * table.length))];
}

// Re-export for consumers that need the box extents (e.g. debug overlays).
export { vehicleLength, vehicleWidth };
